// src/offline/syncEngine.js
// Background "warm cache" engine for offline PWA support.
//
// It issues authenticated GETs for all leads and their related data. Every GET
// flows through the service worker, which caches API responses (network-first)
// and call recordings (cache-first) — so even leads you never opened are
// available offline afterwards. This module never touches the DOM and never
// throws to callers; it logs-and-continues on any failure.

import axios from 'axios';
import { setLastSyncedAt, setSyncStats } from './offlineDb';

const API_URL = process.env.REACT_APP_API_URL; // https://api.mrpatchasphalt.com/api
// Public origin used for static recording files (/recordings/...).
const API_PUBLIC =
  process.env.REACT_APP_API_PUBLIC ||
  (API_URL ? API_URL.replace(/\/api\/?$/, '') : '');

const RECORDING_MAX_AGE_DAYS = 60;
const RECORDING_MAX_AGE_MS = RECORDING_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
const LEAD_CONCURRENCY = 4; // polite parallelism when warming per-lead data
const PERIODIC_INTERVAL_MS = 20 * 60 * 1000; // re-sync every 20 min while open

let syncing = false;
let intervalStarted = false;

const isOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine);
const hasToken = () => {
  try { return !!localStorage.getItem('token'); } catch (e) { return false; }
};

// Fire a GET whose only purpose is to populate the SW cache. Failures are
// swallowed — a single missing endpoint must not abort the whole sync.
const warm = (path) =>
  axios.get(`${API_URL}${path}`).then((r) => r.data).catch(() => null);

// Warm a recording file via a no-cors fetch so the SW stores it in audio cache.
const warmRecording = (recordingUrl) => {
  if (!recordingUrl || !API_PUBLIC) return Promise.resolve();
  const full = recordingUrl.startsWith('http') ? recordingUrl : `${API_PUBLIC}${recordingUrl}`;
  return fetch(full, { mode: 'no-cors' }).then(() => {}).catch(() => {});
};

const isRecentCall = (item) => {
  if (!item || item.type !== 'call' || !item.recordingUrl) return false;
  const ts = item.timestamp || item.createdAt || item.created_at;
  if (!ts) return true; // no date -> keep it, better to cache than miss
  const when = new Date(ts).getTime();
  if (isNaN(when)) return true;
  return Date.now() - when <= RECORDING_MAX_AGE_MS;
};

// Process an array of items with bounded concurrency.
const mapPool = async (items, size, worker) => {
  let i = 0;
  const runners = new Array(Math.min(size, items.length)).fill(0).map(async () => {
    while (i < items.length) {
      const idx = i++;
      await worker(items[idx], idx);
    }
  });
  await Promise.all(runners);
};

const warmLead = async (lead) => {
  const id = lead && (lead.id || lead.lead_id);
  if (!id) return { recordings: 0 };

  // Warm the per-lead detail endpoints (parallel, all best-effort).
  await Promise.all([
    warm(`/leads/${id}/details`),
    warm(`/leads/${id}/jobs`),
    warm(`/leads/${id}/estimates`),
    warm(`/leads/${id}/addresses`),
    warm(`/leads/${id}/phone-numbers`),
    warm(`/leads/${id}/email-addresses`),
    warm(`/lead-logs/lead/${id}`),
  ]);

  // Communication history drives which recordings we download.
  const history = await warm(`/communications/leads/${id}/history`);
  const items = Array.isArray(history) ? history : (history && history.history) || [];
  const recentRecordings = items.filter(isRecentCall);
  await Promise.all(recentRecordings.map((c) => warmRecording(c.recordingUrl)));

  return { recordings: recentRecordings.length };
};

// Run one full sync pass. Guarded so concurrent triggers coalesce.
export const runSync = async () => {
  if (syncing || !isOnline() || !hasToken()) return;
  syncing = true;
  const startedAt = Date.now();
  let recordingCount = 0;
  try {
    // Static lookups + full leads list (the list also powers offline search).
    const [leads] = await Promise.all([
      warm('/leads'),
      warm('/lead-statuses'),
      warm('/lead-origins'),
      warm('/services'),
    ]);

    const leadList = Array.isArray(leads) ? leads : (leads && leads.leads) || [];
    await mapPool(leadList, LEAD_CONCURRENCY, async (lead) => {
      const res = await warmLead(lead);
      recordingCount += res.recordings;
    });

    const finishedAt = Date.now();
    setLastSyncedAt(finishedAt);
    setSyncStats({ leads: leadList.length, recordings: recordingCount, durationMs: finishedAt - startedAt });
    console.log(
      `[offline-sync] warmed ${leadList.length} leads, ${recordingCount} recordings in ${Math.round((finishedAt - startedAt) / 1000)}s`
    );
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('offline-sync-complete'));
    }
  } catch (e) {
    console.warn('[offline-sync] pass failed:', e && e.message);
  } finally {
    syncing = false;
  }
};

// Start the engine: sync on load, on reconnect, and periodically while open.
// Safe to call once (idempotent for the interval/listeners).
export const startSyncEngine = () => {
  // Ask for durable storage so the browser is less likely to evict our caches.
  try { navigator.storage && navigator.storage.persist && navigator.storage.persist(); } catch (e) { /* ignore */ }

  // Initial pass shortly after load (let the app settle first).
  setTimeout(() => { runSync(); }, 4000);

  if (!intervalStarted) {
    intervalStarted = true;
    window.addEventListener('online', () => { runSync(); });
    setInterval(() => { runSync(); }, PERIODIC_INTERVAL_MS);
  }
};
